import * as React from 'react'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export const OUT_OF_DATE_QUARTER_RATE_CODE =
  'TIMESHEET_QUATER_WGE_EXCHANGE_RATE_OUTDATED'
export const MISSING_QUARTER_RATE_CODE =
  'TIMESHEET_QUATER_WGE_EXCHANGE_RATE_MISSING'

/**
 * Serwer zglasza brak kursu na liczony kwartal dopiero wtedy, gdy zadanie NIE niesie
 * zgody na starszy kurs - przy `outOfDateQuarterExchangeRateAccepted: true` sprawdzenie
 * milczy. Dlatego precalculate leci z `false`, a zgode zbieramy tutaj i dopiero z nia
 * powtarzamy przebieg. Tak samo robi WPF (DriverTimesheetGroupViewModel): pyta i dopiero
 * po "tak" ustawia AllowOutOfDateExchangeRates.
 *
 * Kod moze przyjsc jako pozycja listy OperationResult, jako `{ type, error }` albo
 * w ciele odpowiedzi 400 - stad przegladanie wszystkich trzech ksztaltow.
 */
export const hasErrorCode = (payload: any, code: string): boolean => {
  const seen = new Set<any>()

  const walk = (node: any): boolean => {
    if (node == null || typeof node !== 'object') return false
    if (seen.has(node)) return false
    seen.add(node)

    if (Array.isArray(node)) return node.some(walk)
    if (String(node.code ?? '') === code) return true

    return [node.error, node.data, node.response].some(walk)
  }

  return walk(payload)
}

export const hasOutOfDateQuarterRateNotice = (payload: any): boolean =>
  hasErrorCode(payload, OUT_OF_DATE_QUARTER_RATE_CODE)

/**
 * Potwierdzenie liczenia po nieaktualnym kursie kwartalnym. Zamkniecie krzyzykiem =
 * odmowa: kurs sprzed kwartalu zanizy albo zawyzy kwoty zagraniczne, wiec cisza nie
 * moze oznaczac zgody.
 */
export const useOutOfDateRateConfirm = () => {
  const { t } = useLocalize()
  const [isOpen, setIsOpen] = React.useState(false)
  const resolveRef = React.useRef<((confirmed: boolean) => void) | null>(null)

  const settle = React.useCallback((confirmed: boolean) => {
    setIsOpen(false)
    const resolve = resolveRef.current
    resolveRef.current = null
    resolve?.(confirmed)
  }, [])

  const requestConfirm = React.useCallback(() => {
    resolveRef.current?.(false)
    setIsOpen(true)
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const modal = (
    <ConfirmModal
      isOpen={isOpen}
      onClose={() => settle(false)}
      title={t('timesheet.insufficient-data-title')}
      text={t(`errorCode.${OUT_OF_DATE_QUARTER_RATE_CODE}`)}
      actionText={t('timesheet.calculate-anyway')}
      onConfirm={() => settle(true)}
    />
  )

  return { requestConfirm, modal }
}
